import React, { useEffect, useMemo } from 'react';
import { Modal, Accordion } from 'react-bootstrap-5';
import { get } from 'helpers/api';

import { faUser, faUserGroup } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import type { DocTemplate } from './document_index/types';
import SearchInput from 'components/search_sidebar/sidebar_search';

interface TemplateAccessReportModalProps {
  documentTemplate: DocTemplate;
  onHide: () => void;
}

// User or Team
interface AccessableItem {
  id: number;
  name: string;
}

export default function TemplateAccessReportModal({ documentTemplate, onHide } : TemplateAccessReportModalProps) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [reportData, setReportData] = React.useState<{ teams?: AccessableItem[]; users?: AccessableItem[] }>({});
  const [searchText, setSearchText] = React.useState<string>('');

  const teamsToDisplay : AccessableItem[] | undefined = useMemo(
    () => reportData?.teams?.filter(t => t.name.toLowerCase().includes(searchText.toLowerCase())),
    [reportData?.teams, searchText]
  );
  const usersToDisplay : AccessableItem[] | undefined = useMemo(
    () => reportData?.users?.filter(u => u.name.toLowerCase().includes(searchText.toLowerCase())),
    [reportData?.users, searchText]
  );

  useEffect(() => {
    setLoading(true);
    get(`/corporate/document_defaults/document_templates/${documentTemplate.id}/access_report`)
      .then((response) => {
        // Handle the response data here
        console.log('Access report data:', response);
        setReportData(response);
      })
      .catch((error) => {
        // Handle any errors here
        console.error('Error fetching access report:', error);
        setError('Error fetching access report');
      }).finally(() => {
        setLoading(false);
      });
  }, [documentTemplate]);

  const list = (collection : AccessableItem[] | undefined, label: string, icon: IconDefinition) => {
    if (!collection) return null;

    if (loading) return (
      <div>Loading...</div>
    );

    return (
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <div>
            <Accordion.Button className="bg-light py-2">{label}</Accordion.Button>
          </div>
          <Accordion.Body className="bg-white px-2">
            {collection.length === 0 ? (
              <div className="d-flex align-items-center gap-2 p-2">
                <FontAwesomeIcon className="text-muted" icon={icon}/>
                <span className="text-muted">No {label.toLowerCase()} selected</span>
              </div>
            ) : collection.map((item, idx) => (
              <div className="d-flex align-items-center gap-2 p-2" key={idx}>
                <FontAwesomeIcon className="text-muted" icon={icon}/>
                {item.name}
              </div>
            ))}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );
  };

  const teamList = useMemo(() => list(teamsToDisplay, 'Teams', faUserGroup), [teamsToDisplay, loading]);
  const userList = useMemo(() => list(usersToDisplay, 'Individuals', faUser), [usersToDisplay, loading]);

  const body = useMemo(() => {
    if (error) return <div className="text-danger">{error}</div>;

    return (
      <div>
        <SearchInput
          placeholder="Search for teams or individuals"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />

        <div className="text-muted py-2">Who has access</div>

        {teamList}
        {userList}
      </div>
    );
  }, [error, searchText, teamList, userList]);

  return (
    <Modal show onHide={onHide}>

      <Modal.Header closeButton>
        <Modal.Title>Access Report</Modal.Title>
        <div className="text-muted fs-small">
          A list of teams and users who have access to this document
        </div>
      </Modal.Header>

      <Modal.Body>
        {body}
      </Modal.Body>
    </Modal>
  );
}
